import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  FileText, Download, Calendar, TrendingUp, TrendingDown,
  ArrowRight, CheckCircle, XCircle
} from 'lucide-react';
import { getForecasts, exportForecastReport } from '../services/api';
import { format } from 'date-fns';

function Reports() {
  const [forecasts, setForecasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);

  // Demo comparison data
  const comparisonData = [
    { period: 'Jan', actual: 45000, predicted: 44500, variance: 500 },
    { period: 'Feb', actual: 52000, predicted: 51000, variance: 1000 },
    { period: 'Mar', actual: 48000, predicted: 49000, variance: -1000 },
    { period: 'Apr', actual: 61000, predicted: 58000, variance: 3000 },
    { period: 'May', actual: 55000, predicted: 56500, variance: -1500 },
    { period: 'Jun', actual: 67000, predicted: 65000, variance: 2000 },
  ];

  const accuracyHistory = [
    { month: 'Jan', accuracy: 92.5 },
    { month: 'Feb', accuracy: 93.1 },
    { month: 'Mar', accuracy: 91.8 },
    { month: 'Apr', accuracy: 94.2 },
    { month: 'May', accuracy: 95.0 },
    { month: 'Jun', accuracy: 94.8 },
  ];

  const reportSummary = {
    totalForecasts: 24,
    avgAccuracy: 93.6,
    bestPerforming: 'Revenue Forecast',
    improvementRate: '+5.2%'
  };

  useEffect(() => {
    loadForecasts();
  }, []);

  const loadForecasts = async () => {
    try {
      setLoading(true);
      const response = await getForecasts({ status: 'completed' }).catch(() => ({ forecasts: [] }));
      setForecasts(response.forecasts || []);
    } catch (err) {
      console.error('Error loading forecasts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = async (forecastId) => {
    try {
      const report = await exportForecastReport(forecastId);
      const blob = new Blob([JSON.stringify(report.report, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `forecast_report_${format(new Date(), 'yyyy-MM-dd')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handleExportAll = () => {
    const reportData = {
      generatedAt: new Date().toISOString(),
      summary: reportSummary,
      comparisonData,
      accuracyHistory,
      forecasts: forecasts.map(f => ({
        id: f._id,
        type: f.predictionType,
        accuracy: f.metrics?.accuracy,
        createdAt: f.createdAt
      }))
    };

    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics_report_${format(new Date(), 'yyyy-MM-dd')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="reports-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2>Reports & Analysis</h2>
          <p>Compare actual vs predicted values and track forecast accuracy</p>
        </div>
        <button className="btn btn-primary" onClick={handleExportAll}>
          <Download size={18} />
          Export Full Report
        </button>
      </div>

      {/* Summary Stats */}
      <div className="kpi-grid" style={{ marginBottom: '1.5rem' }}>
        <div className="kpi-card">
          <div className="kpi-icon blue">
            <FileText size={24} />
          </div>
          <div className="kpi-content">
            <p className="kpi-label">Total Forecasts</p>
            <h3 className="kpi-value">{reportSummary.totalForecasts}</h3>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon green">
            <CheckCircle size={24} />
          </div>
          <div className="kpi-content">
            <p className="kpi-label">Avg Accuracy</p>
            <h3 className="kpi-value">{reportSummary.avgAccuracy}%</h3>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon purple">
            <TrendingUp size={24} />
          </div>
          <div className="kpi-content">
            <p className="kpi-label">Best Performing</p>
            <h3 className="kpi-value" style={{ fontSize: '1.25rem' }}>{reportSummary.bestPerforming}</h3>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon yellow">
            <ArrowRight size={24} />
          </div>
          <div className="kpi-content">
            <p className="kpi-label">Improvement</p>
            <h3 className="kpi-value" style={{ color: '#10b981' }}>{reportSummary.improvementRate}</h3>
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom: '1.5rem' }}>
        {/* Actual vs Predicted Chart */}
        <div className="chart-container">
          <div className="chart-header">
            <h3 className="chart-title">Actual vs Predicted Comparison</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={comparisonData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="period" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `$${v/1000}k`} />
              <Tooltip
                formatter={(value) => formatCurrency(value)}
                contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              />
              <Legend />
              <Bar dataKey="actual" fill="#10b981" name="Actual" radius={[4, 4, 0, 0]} />
              <Bar dataKey="predicted" fill="#3b82f6" name="Predicted" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Accuracy Trend */}
        <div className="chart-container">
          <div className="chart-header">
            <h3 className="chart-title">Forecast Accuracy Trend</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={accuracyHistory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} domain={[85, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip
                formatter={(value) => [`${value}%`, 'Accuracy']}
                contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              />
              <Line
                type="monotone"
                dataKey="accuracy"
                stroke="#8b5cf6"
                strokeWidth={3}
                dot={{ r: 6, fill: '#8b5cf6' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Variance Analysis */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">Variance Analysis</h3>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Period</th>
                <th>Actual</th>
                <th>Predicted</th>
                <th>Variance</th>
                <th>Variance %</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {comparisonData.map((row, i) => {
                const variancePercent = ((row.variance / row.predicted) * 100).toFixed(1);
                const isPositive = row.variance >= 0;
                const isWithinThreshold = Math.abs(parseFloat(variancePercent)) < 5;

                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 500 }}>{row.period}</td>
                    <td>{formatCurrency(row.actual)}</td>
                    <td>{formatCurrency(row.predicted)}</td>
                    <td style={{ color: isPositive ? '#10b981' : '#ef4444' }}>
                      {isPositive ? '+' : ''}{formatCurrency(row.variance)}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        {isPositive ? <TrendingUp size={14} color="#10b981" /> : <TrendingDown size={14} color="#ef4444" />}
                        <span style={{ color: isPositive ? '#10b981' : '#ef4444' }}>
                          {isPositive ? '+' : ''}{variancePercent}%
                        </span>
                      </div>
                    </td>
                    <td>
                      {isWithinThreshold ? (
                        <span className="badge badge-success">
                          <CheckCircle size={12} style={{ marginRight: 4 }} />
                          Within Range
                        </span>
                      ) : (
                        <span className="badge badge-warning">
                          <XCircle size={12} style={{ marginRight: 4 }} />
                          Review
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Reports */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Forecast History</h3>
        </div>

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Report</th>
                  <th>Type</th>
                  <th>Model</th>
                  <th>Accuracy</th>
                  <th>MAPE</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {forecasts.length > 0 ? forecasts.map((forecast) => (
                  <tr key={forecast._id}>
                    <td style={{ fontWeight: 500 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FileText size={18} color="#6b7280" />
                        {forecast.dataset?.name || 'Forecast Report'}
                      </div>
                    </td>
                    <td style={{ textTransform: 'capitalize' }}>{forecast.predictionType}</td>
                    <td>{forecast.modelUsed?.replace('_', ' ')}</td>
                    <td>
                      <span style={{
                        fontWeight: 600,
                        color: forecast.metrics?.accuracy > 90 ? '#10b981' : '#f59e0b'
                      }}>
                        {forecast.metrics?.accuracy?.toFixed(1)}%
                      </span>
                    </td>
                    <td>{forecast.metrics?.mape?.toFixed(2)}%</td>
                    <td>{format(new Date(forecast.createdAt), 'MMM d, yyyy')}</td>
                    <td>
                      <button
                        className="btn btn-outline"
                        onClick={() => handleExportReport(forecast._id)}
                        style={{ padding: '0.375rem 0.75rem' }}
                      >
                        <Download size={16} />
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                      No forecast reports available. Generate a forecast to see reports here.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Reports;
